import { Dispatch, SetStateAction } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
    Modal,
    Button,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCreateTicketMutation } from "../service/main";
import { toast } from "react-toastify";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
    refetch?: () => void;
}

const validationSchema = Yup.object({
    subject: Yup.string()
        .required("Subject is required")
        .max(80, "Subject cannot exceed 80 characters")
        .matches(/^\S/, "Subject cannot start with a space"),
    category: Yup.string().required("Category is required"),
    priority: Yup.mixed().required("Priority is required"),
    description: Yup.string()
        .required("Description is required")
        .min(10, "Description must be at least 10 characters")
        .max(800, "Description cannot exceed 800 characters")
        .matches(/^\S/, "Description cannot start with a space"),
});

export default function AddTicket({
    open,
    onClose,
    setOpen,
    refetch,
}: ModalProps) {
    const [createTicket, { isLoading }] = useCreateTicketMutation();

    const formik = useFormik({
        initialValues: {
            subject: "",
            category: "",
            priority: "",
            description: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                const body = {
                    ...values,
                    priority: Number(values.priority),
                };
                const response = await createTicket({ body }).unwrap();
                if (response.statusCode === 200 || response.statusCode === 201) {
                    toast.success(response.message || "Ticket created successfully");
                    setOpen(false);
                    formik.resetForm();
                    if (refetch) refetch();
                } else {
                    toast.error(response.message || "Failed to create ticket");
                }
            } catch (error: any) {
                toast.error(error?.data?.message || "Something went wrong");
            }
        },
    });

    const handleClose = () => {
        setOpen(false);
        formik.resetForm();
        onClose();
    };

    return (
        <Modal className="modal addReply_modal" open={open} onClose={handleClose}>
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>Add Ticket</h2>
                        <button type="button" className="btn-close" onClick={handleClose}>
                            <CloseIcon />
                        </button>
                    </div>

                    <form className="form" onSubmit={formik.handleSubmit}>
                        {/* Subject Field */}
                        <div className="control_group">
                            <label htmlFor="subject">Subject</label>
                            <TextField
                                id="subject"
                                name="subject"
                                placeholder="Brief description of your issue"
                                fullWidth
                                hiddenLabel
                                value={formik.values.subject}
                                inputProps={{ maxLength: 800 }}
                                onChange={(e) => {
                                    if (e.target.value.startsWith(" ")) return;
                                    formik.handleChange(e);
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.subject && Boolean(formik.errors.subject)}
                                helperText={formik.touched.subject && formik.errors.subject}
                            />
                        </div>

                        {/* Category Dropdown */}
                        <div className="control_group">
                            <label htmlFor="category">Category</label>
                            <FormControl
                                fullWidth
                                error={
                                    formik.touched.category && Boolean(formik.errors.category)
                                }
                            >
                                <Select
                                    id="category"
                                    name="category"
                                    value={formik.values.category}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    displayEmpty
                                >
                                    <MenuItem value="">Select Category</MenuItem>
                                    <MenuItem value="Technical Issue">Technical Issue</MenuItem>
                                    <MenuItem value="Billing Issue">Billing Issue</MenuItem>
                                    <MenuItem value="Account Issue">Account Issue</MenuItem>
                                    <MenuItem value="General Inquiry">General Inquiry</MenuItem>
                                </Select>
                                {formik.touched.category && formik.errors.category && (
                                    <FormHelperText>{formik.errors.category}</FormHelperText>
                                )}
                            </FormControl>
                        </div>

                        {/* Priority Dropdown */}
                        <div className="control_group">
                            <label htmlFor="priority">Priority</label>
                            <FormControl
                                fullWidth
                                error={
                                    formik.touched.priority && Boolean(formik.errors.priority)
                                }
                            >
                                <Select
                                    id="priority"
                                    name="priority"
                                    value={formik.values.priority}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    displayEmpty
                                >
                                    <MenuItem value="">Select Priority</MenuItem>
                                    <MenuItem value={1}>Low</MenuItem>
                                    <MenuItem value={2}>Medium</MenuItem>
                                    <MenuItem value={3}>High</MenuItem>
                                </Select>
                                {formik.touched.priority && formik.errors.priority && (
                                    <FormHelperText>{formik.errors.priority}</FormHelperText>
                                )}
                            </FormControl>
                        </div>

                        {/* Description Field */}
                        <div className="control_group">
                            <label htmlFor="description">Description</label>
                            <TextField
                                id="description"
                                name="description"
                                placeholder="Please provide detailed information about your issue..."
                                fullWidth
                                hiddenLabel
                                multiline
                                rows={4}
                                value={formik.values.description}
                                inputProps={{ maxLength: 800 }}
                                onChange={(e) => {
                                    if (e.target.value.startsWith(" ")) return;
                                    formik.handleChange(e);
                                }}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.description &&
                                    Boolean(formik.errors.description)
                                }
                                helperText={
                                    formik.touched.description && formik.errors.description
                                }
                            />
                        </div>

                        {/* Footer Buttons */}
                        <div className="footer_btn_flex">
                            <Button
                                className="btn btn_grey"
                                onClick={handleClose}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="btn btn_primary"
                                disabled={isLoading}
                            >
                                {isLoading ? "Submitting..." : "Submit Ticket"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
}
